import { LocationComposite } from './LocationComposite'
import { LocationFactory } from './LocationFactory'
import { LocalStorageHandler } from '@/utils/localStorageHandler'
import { LocationObserver } from './LocationObserver'
import { Location, RecentLocation } from './types'
import dayjs from 'src/utils/dayjs'
import { fetchCurrentConditions } from '@/services/api/bff/weather/Weather.service'
import { fetchPlace } from '@/services/api/bff/places/Places.service'

const WETTER_PREFIX = /\/wetter\/[a-zA-Z]\//
const CACHE_EXPIRATION = 5 * 60 * 1000

export class LocationManager {
  private static instance: LocationManager
  readonly localStorageHandler: LocalStorageHandler
  private readonly composite: LocationComposite
  readonly observer: LocationObserver
  readonly recentLocationKey = 'recent_location'
  private isInitialized = false
  private listeners: Array<() => void> = []

  private constructor() {
    this.localStorageHandler = new LocalStorageHandler('wt:')
    this.composite = new LocationComposite()
    this.observer = new LocationObserver()
  }

  public static getInstance(): LocationManager {
    if (!LocationManager.instance) {
      LocationManager.instance = new LocationManager()
    }
    return LocationManager.instance
  }

  /**
   * Initializes the LocationManager. Ensures it only runs on the client side.
   */
  public async init(): Promise<void> {
    if (this.isInitialized) return
    if (typeof window === 'undefined') {
      console.warn('LocationManager cannot be initialized on the server.')
      return
    }

    try {
      await this.loadLocations()
      this.observer.init()
      this.observer.registerCallback((url: string) => this.handleUrlChange(url))
      this.isInitialized = true
    } catch (error) {
      console.error('Error during LocationManager initialization:', error)
    }
  }

  private persist(): void {
    this.localStorageHandler.setItem(this.recentLocationKey, {
      data: this.composite.getLocations(),
    })
    this.notify()
  }

  public async addLocation(location: Location): Promise<void> {
    this.composite?.addLocation(location)
    this.persist()
  }

  private removeDuplicates(items: Location[]): Location[] {
    const seen = new Set()
    return items.filter((location: Location) => {
      if (!seen.has(location.geohash)) {
        seen.add(location.geohash)
        return true
      }
      return false
    })
  }

  private async loadLocations(): Promise<void> {
    const saved = this.localStorageHandler.getItem<RecentLocation>(
      this.recentLocationKey,
    )
    let items: Location[] = []
    let current = null

    if (saved?.data?.length) {
      items.push(...saved.data)
      current = saved.data[0]
    }

    if (items.length === 0) {
      items = LocationFactory.createDefaultLocations()
    }

    const unique = this.removeDuplicates(items)

    const sorted = unique.toSorted(
      (a, b) => (b.lastVisit ?? 0) - (a.lastVisit ?? 0),
    )
    const topFive = sorted.slice(0, 5)
    const rest = sorted.slice(5)

    const hydratedFive = await Promise.all(
      topFive.map(async (loc) => {
        // only hydrate if missing data
        if (!loc.fetchedPlace || !loc.fetchedCurrentConditions) {
          try {
            const up = await this.populateLocation(loc)
            return up ?? loc
          } catch {
            return loc
          }
        }
        return loc
      }),
    )

    const finalList = [...hydratedFive, ...rest]

    this.composite.setLocations(finalList)
    this.composite.setCurrentLocation(current ?? finalList[0])

    this.persist()
  }

  private async handleUrlChange(url: string): Promise<void> {
    if (!WETTER_PREFIX.test(url)) return

    const match = RegExp(/\/wetter\/(.)\/(\d+)/).exec(url)
    if (!match) return

    let location = this.composite?.findLocationByUrl(url)

    this.composite.removeDefaultLocations()

    if (
      !location ||
      this.isCacheExpired(location.lastFetchedCurrentConditions)
    ) {
      location = LocationFactory.createLocation({ url, visits: 1 })
      const populated = await this.populateLocation(location)
      if (location) {
        this.composite?.addLocation(location)
        location = populated
      }
    } else {
      location.lastVisit = Date.now()
      location.visits = (location.visits || 0) + 1
    }

    this.composite.setCurrentLocation(location)
    this.persist()
  }

  private async populateLocation(location: Location) {
    if (
      location.lastFetchedCurrentConditions &&
      !this.isCacheExpired(location.lastFetchedCurrentConditions)
    ) {
      return location
    }

    try {
      const [, osmType, osmId] = location.url.split('/').slice(1)
      const place = await fetchPlace(osmType, osmId)
      if (!place) {
        console.error(`Failed to fetch place for URL: ${location.url}`)
        return null
      }
      const data = place.data.attributes
      location.fetchedPlace = data
      location.lastFetchedPlace = Date.now()
      location.timezone = data.timezone.name
      location.timezoneOffset = data.timezone.offset
      location.localTime = dayjs().tz(data.timezone.name).format('HH:mm')
      location.geohash = data.geohash.substring(0, 5)

      const currentConditions = await fetchCurrentConditions(location.geohash)

      if (!currentConditions) {
        console.error(
          `Failed to fetch current conditions for geohash: ${location.geohash}`,
        )
        return null
      }

      location.fetchedCurrentConditions = currentConditions.data
      location.lastFetchedCurrentConditions = Date.now()

      return location
    } catch (error) {
      console.error(`Failed to fetch current conditions: ${error}`)
      return location
    }
  }

  private isCacheExpired(lastFetched: number | null): boolean {
    if (!lastFetched) return true
    const now = Date.now()
    return now - lastFetched > CACHE_EXPIRATION
  }

  public getTopFiveLocations(): Location[] {
    return this.composite?.getTopLocations(5) || []
  }

  public async populateTopFiveLocations(): Promise<Location[]> {
    const list = this.getTopFiveLocations()
    const results = await Promise.all(
      list.map((loc) => this.populateLocation(loc)),
    )
    const filtered = results.filter((l): l is Location => !!l)
    this.composite.setLocations(filtered)
    this.persist()
    return filtered
  }

  public getCurrentLocation(): Location | null {
    return this.composite?.getCurrentLocation() || null
  }

  public subscribe(fn: () => void) {
    this.listeners.push(fn)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn)
    }
  }

  private notify() {
    this.listeners.forEach((fn) => fn())
  }
}
